import React from 'react';
import { C } from '../data/constants';
import {
  TrendingUp, CreditCard, PiggyBank, Shield,
  Target, AlertTriangle, BarChart2, Zap,
  CheckCircle, ArrowRight, DollarSign, Layers,
  Calendar, Award, BookOpen, Activity
} from 'lucide-react';

// ─── Shared primitives ─────────────────────────────────────────────────────────
const IG = { bg: C.bg, card: '#fff', navy: C.navy, teal: C.teal, mint: C.mint, gold: C.gold, coral: C.coral, purple: C.purple, border: C.border };

function InfoCard({ children, style = {} }) {
  return (
    <div style={{ background: IG.card, border: `1px solid ${IG.border}`, borderRadius: 12, padding: '18px 20px', ...style }}>
      {children}
    </div>
  );
}

function BigNum({ value, label, color = IG.teal, sub = '' }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontFamily: "'DM Serif Display',serif", fontSize: 36, color, lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{sub}</div>}
      <div style={{ fontSize: 12, color: C.body, marginTop: 4, fontWeight: 500 }}>{label}</div>
    </div>
  );
}

function HBar({ label, pct, color, value }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
        <span style={{ fontSize: 13, color: C.text }}>{label}</span>
        <span style={{ fontSize: 12, fontWeight: 700, color }}>{value}</span>
      </div>
      <div style={{ height: 8, background: C.border, borderRadius: 4 }}>
        <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 4 }} />
      </div>
    </div>
  );
}

function Pill({ icon: Icon, text, color }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: color + '18', border: `1px solid ${color}44`, borderRadius: 8, padding: '8px 14px' }}>
      {Icon && <Icon size={14} color={color} />}
      <span style={{ fontSize: 13, color: C.text, fontWeight: 500 }}>{text}</span>
    </div>
  );
}

function SectionTitle({ children, color = IG.navy }) {
  return <div style={{ fontFamily: "'DM Serif Display',serif", fontSize: 14, color, fontWeight: 400, marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{children}</div>;
}

// ─── WEEK 1 — Financial Foundations ──────────────────────────────────────────
function Week1Infographic() {
  const topics = [
    {
      num: 1,
      title: 'Budgeting Basics',
      story: 'Meet Chris, who earns $4,000/month but always runs out of money. No budget, no plan. After tracking expenses, Chris discovers $800 goes to eating out and subscriptions they forgot about.',
      lesson: 'A budget is simply telling your money where to go instead of wondering where it went. The 50/30/20 rule: 50% needs, 30% wants, 20% savings.'
    },
    {
      num: 2,
      title: 'Compound Interest',
      story: 'Sarah starts investing $200/month at 25. Jake waits until 35, same amount. By 65, Sarah has $602K, Jake has $244K — Sarah invested only $24K more but ended with $358K more.',
      lesson: 'Your money earns returns, then those returns earn returns. Time is your biggest advantage. Start early, even with small amounts.'
    },
    {
      num: 3,
      title: 'Inflation & Purchasing Power',
      story: 'In 1995, $100 bought you groceries for a week. Today, that same $100 barely covers 3 days. Your grandparents paid $3,000 for their first car — today that\'s a down payment.',
      lesson: 'Money sitting in a savings account loses ~3% of its value yearly to inflation. $100 today will only buy $74 worth of goods in 10 years.'
    },
    {
      num: 4,
      title: 'Opportunity Costs',
      story: 'Emma buys a $5 coffee daily instead of making it at home. Seems small, but that\'s $1,825/year. Invested at 8%, that coffee habit costs her $75,000 over 30 years.',
      lesson: 'Every dollar spent is a dollar not invested. Every choice has a hidden cost — what are you giving up? Ask: "Is this worth my future dollars?"'
    },
    {
      num: 5,
      title: 'Net Worth Calculation',
      story: 'Marcus thought he was doing great with a $50K salary and a new car. But the car loan, student debt, and credit cards meant his net worth was actually -$15,000.',
      lesson: 'Net Worth = What you Own - What you Owe. This number matters more than your income. Track it quarterly to see if you\'re moving forward or backward.'
    },
    {
      num: 6,
      title: 'Financial Goal-Setting',
      story: 'Priya says "I want to save more money" every January. By March, nothing changes. Then she tries: "Save $500/month for 12 months = $6,000 emergency fund by Dec 31." She hits it.',
      lesson: 'Vague goals fail. Make them SMART: Specific, Measurable, Achievable, Relevant, Time-bound. "Save more" becomes "Save $X by Y date for Z reason."'
    }
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Header */}
      <div style={{ background: IG.navy, borderRadius: 12, padding: '28px 32px', textAlign: 'center' }}>
        <div style={{ fontSize: 12, color: C.mint, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 8 }}>Week 1</div>
        <div style={{ fontFamily: "'DM Serif Display',serif", fontSize: 32, color: '#fff', fontWeight: 400, marginBottom: 8 }}>Financial Foundations</div>
        <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.75)' }}>6 core concepts to build your money mindset</div>
      </div>

      {/* All Topics */}
      {topics.map(topic => (
        <InfoCard key={topic.num} style={{ borderLeft: `4px solid ${IG.teal}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
            <div style={{ width: 32, height: 32, borderRadius: '50%', background: IG.teal, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, flexShrink: 0 }}>{topic.num}</div>
            <div style={{ fontFamily: "'DM Serif Display',serif", fontSize: 18, color: IG.navy }}>{topic.title}</div>
          </div>
          <div style={{ background: '#f0fdf4', borderRadius: 8, padding: '14px 16px', marginBottom: 12 }}>
            <div style={{ fontSize: 13, color: C.text, lineHeight: 1.7, marginBottom: 8 }}>📖 {topic.story}</div>
          </div>
          <div style={{ fontSize: 14, color: C.text, lineHeight: 1.7, paddingLeft: 12 }}>
            <strong>💡 The Lesson:</strong> {topic.lesson}
          </div>
        </InfoCard>
      ))}
    </div>
  );
}

// ─── WEEK 2 — Borrowing & Debt Management ────────────────────────────────────
function Week2Infographic() {
  const topics = [
    {
      num: 1,
      title: 'Credit Scores & Reports',
      story: 'Alex pays bills late occasionally and keeps credit cards maxed out. When applying for a car loan, the rate is 18% instead of 6%. Over 5 years, Alex pays $8,000 extra in interest — the cost of not understanding credit scores.',
      lesson: 'Your credit score (300-850) determines loan rates. Pay on time (35% of score), keep balances under 30% of limits (30% of score). A 100-point difference can cost you $50,000+ lifetime.'
    },
    {
      num: 2,
      title: 'Interest Rates Explained',
      story: 'Maya has a $5,000 credit card balance at 24% APR. She pays the minimum ($150/month). It takes 5 years to pay off and costs $11,045 total — she paid $6,045 in interest alone, more than the original debt.',
      lesson: 'APR = Annual Percentage Rate. Credit cards often charge 20-25%. Even "low" 6% adds up fast. Always look at total cost, not just monthly payment.'
    },
    {
      num: 3,
      title: 'Debt Repayment Strategies',
      story: 'Jordan has 3 debts: $1,000 at 8%, $5,000 at 18%, $500 at 5%. Pays smallest first (Snowball method) for quick wins. Ben tackles the 18% first (Avalanche method) and saves $800 in interest.',
      lesson: 'Snowball = smallest balance first (motivational wins). Avalanche = highest interest first (saves most money). Pick what keeps you consistent.'
    },
    {
      num: 4,
      title: 'Good Debt vs Bad Debt',
      story: 'Sam borrows $30K for a nursing degree, gets a $65K/year job (good debt — increases earning power). Taylor borrows $25K for a luxury car that depreciates to $15K in 3 years (bad debt — loses value).',
      lesson: 'Good debt: appreciates or increases income (mortgage, student loans for in-demand fields, business). Bad debt: depreciates or high-interest consumption (payday loans, credit cards for lifestyle).'
    },
    {
      num: 5,
      title: 'Credit Card Psychology',
      story: 'Studies show people spend 12-18% more when using credit cards vs cash. Riley budgets $300 for groceries but swipes a card mindlessly and spends $380. The pain of payment is delayed, so spending feels easier.',
      lesson: 'Credit cards divorce spending from the pain of losing money. Combat this: treat credit like debit, pay off weekly, or use cash for discretionary spending.'
    },
    {
      num: 6,
      title: 'Loan Types & Terms',
      story: 'Morgan needs $10K. Payday loan: 400% APR, pay back $14,000 in months. Personal loan: 12% APR, pay back $11,200 over 2 years. Credit union loan: 8% APR, pay back $10,800. Same need, wildly different costs.',
      lesson: 'Know your options: payday (avoid!), personal loans, credit union loans, 0% credit card transfers. Shop around — rates vary 10-40% between lenders.'
    }
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Header */}
      <div style={{ background: IG.coral, borderRadius: 12, padding: '28px 32px', textAlign: 'center' }}>
        <div style={{ fontSize: 12, color: '#fff', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', opacity: 0.85, marginBottom: 8 }}>Week 2</div>
        <div style={{ fontFamily: "'DM Serif Display',serif", fontSize: 32, color: '#fff', fontWeight: 400, marginBottom: 8 }}>Borrowing & Debt</div>
        <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.8)' }}>Understanding credit and managing what you owe</div>
      </div>

      {/* All Topics */}
      {topics.map(topic => (
        <InfoCard key={topic.num} style={{ borderLeft: `4px solid ${IG.coral}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
            <div style={{ width: 32, height: 32, borderRadius: '50%', background: IG.coral, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, flexShrink: 0 }}>{topic.num}</div>
            <div style={{ fontFamily: "'DM Serif Display',serif", fontSize: 18, color: IG.navy }}>{topic.title}</div>
          </div>
          <div style={{ background: '#fff7ed', borderRadius: 8, padding: '14px 16px', marginBottom: 12 }}>
            <div style={{ fontSize: 13, color: C.text, lineHeight: 1.7, marginBottom: 8 }}>📖 {topic.story}</div>
          </div>
          <div style={{ fontSize: 14, color: C.text, lineHeight: 1.7, paddingLeft: 12 }}>
            <strong>💡 The Lesson:</strong> {topic.lesson}
          </div>
        </InfoCard>
      ))}
    </div>
  );
}

// ─── WEEK 3 — Building Assets ─────────────────────────────────────────────────
function Week3Infographic() {
  const topics = [
    {
      num: 1,
      title: 'Emergency Fund Basics',
      story: 'Jordan gets a 20% raise and immediately upgrades their lifestyle. No emergency fund saved. Six months later, layoffs hit. Jordan is unemployed for 3 months, maxes out credit cards, borrows from family, breaks lease. Total damage: $12,000 in new debt.',
      lesson: 'Build 3-6 months of expenses in a High-Yield Savings Account (HYSA) BEFORE aggressive investing. Stable job = 3 months. Variable income or single earner = 6 months. Self-employed = 9-12 months.'
    },
    {
      num: 2,
      title: 'Investment Accounts (401k, IRA)',
      story: 'Casey\'s employer matches 401(k) contributions up to 5%, but Casey only contributes 2%. That\'s leaving 3% ($1,800/year on a $60K salary) on the table — free money rejected. Over 30 years with growth, that\'s $200K+ lost.',
      lesson: '401(k) = pre-tax, employer match (free money!). Roth IRA = after-tax, grows tax-free. Priority: contribute enough to 401(k) to get full employer match first, then max Roth IRA ($7,000/year).'
    },
    {
      num: 3,
      title: 'Stocks vs Bonds',
      story: 'At 25, Mia puts everything in bonds (safe, low return ~4%/year). At 25, Leo invests 90% stocks (volatile, averages ~10%/year). By 65, Mia has $400K. Leo has $1.8M from the same contributions — because he could handle short-term drops.',
      lesson: 'Stocks = higher long-term returns but volatile. Bonds = stable but lower returns. Young? Go heavy stocks (time to recover from crashes). Retiring soon? Shift toward bonds for stability.'
    },
    {
      num: 4,
      title: 'Index Funds & Diversification',
      story: 'Zoe picks 5 individual tech stocks thinking she\'ll beat the market. Three tank, one breaks even, one doubles. Net gain: 8%. Her friend buys an S&P 500 index fund (owns 500 companies automatically) and gets 11% with zero research.',
      lesson: 'Index funds = instant diversification, low fees (0.03% vs 1%+ for active funds). 92% of fund managers underperform the index over 20 years. Boring beats brilliant.'
    },
    {
      num: 5,
      title: 'Risk & Return Trade-offs',
      story: 'During 2020 market crash, stocks dropped 30%. Ava panicked and sold everything, locking in losses. Ben held tight. By 2021, the market recovered +50%. Ava missed the recovery, Ben made huge gains. Same crash, different outcomes.',
      lesson: 'Higher potential return = higher risk. Stocks can drop 50% in a year but average 10% long-term. Only invest money you won\'t need for 5+ years. Volatility is the price you pay for returns.'
    },
    {
      num: 6,
      title: 'Retirement Planning',
      story: 'At 30, Riley thinks "retirement is 35 years away, I\'ll start later." By 40, Riley finally invests. Meanwhile, Sam started at 25 with small amounts. By 65, Sam has 2.5x more money than Riley despite similar income — because of 15 extra years of compounding.',
      lesson: 'The "magic number" = 25x your annual expenses. Need $40K/year to live? Target $1M saved. Start now, even with $50/month. Time in the market > timing the market.'
    }
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Header */}
      <div style={{ background: `linear-gradient(135deg, ${IG.teal}, ${IG.mint})`, borderRadius: 12, padding: '28px 32px', textAlign: 'center' }}>
        <div style={{ fontSize: 12, color: '#fff', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', opacity: 0.9, marginBottom: 8 }}>Week 3</div>
        <div style={{ fontFamily: "'DM Serif Display',serif", fontSize: 32, color: '#fff', fontWeight: 400, marginBottom: 8 }}>Building Assets</div>
        <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.85)' }}>Make your money work for you while you sleep</div>
      </div>

      {/* All Topics */}
      {topics.map(topic => (
        <InfoCard key={topic.num} style={{ borderLeft: `4px solid ${IG.teal}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
            <div style={{ width: 32, height: 32, borderRadius: '50%', background: IG.teal, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, flexShrink: 0 }}>{topic.num}</div>
            <div style={{ fontFamily: "'DM Serif Display',serif", fontSize: 18, color: IG.navy }}>{topic.title}</div>
          </div>
          <div style={{ background: '#f0fdfa', borderRadius: 8, padding: '14px 16px', marginBottom: 12 }}>
            <div style={{ fontSize: 13, color: C.text, lineHeight: 1.7, marginBottom: 8 }}>📖 {topic.story}</div>
          </div>
          <div style={{ fontSize: 14, color: C.text, lineHeight: 1.7, paddingLeft: 12 }}>
            <strong>💡 The Lesson:</strong> {topic.lesson}
          </div>
        </InfoCard>
      ))}
    </div>
  );
}

// ─── WEEK 4 — Protection & Planning ──────────────────────────────────────────
function Week4Infographic() {
  const topics = [
    {
      num: 1,
      title: 'Types of Insurance',
      story: 'Taylor, 32 and healthy, skips disability insurance to save $80/month. A year later, a car accident leaves Taylor unable to work for 8 months. With no income replacement, Taylor loses $56,000, burns through savings, and goes $15,000 into debt.',
      lesson: '4 critical types: Health (one hospital stay = $30K+), Disability (1 in 4 workers become disabled before retirement), Life (if others depend on your income), Auto/Property (legally/lender required). Disability often overlooked but critical.'
    },
    {
      num: 2,
      title: 'Risk Management Basics',
      story: 'Dana drives an old car worth $3,000 but pays $150/month for full coverage insurance. Meanwhile, Dana has no life insurance despite supporting a spouse and two kids. One protects a depreciating asset, the other protects a family\'s entire financial future.',
      lesson: 'Insure what you can\'t afford to lose. Ask: "If this happened tomorrow, could I cover it without going into debt?" If no, insure it. Prioritize income protection over stuff protection.'
    },
    {
      num: 3,
      title: 'Estate Planning Intro',
      story: 'Pat dies suddenly at 45 with no will. The court decides who gets the house, the savings, custody of minor kids — a process taking 2 years and costing the family $30,000 in legal fees. A simple will would have cost $500 and avoided all the chaos.',
      lesson: 'Estate planning isn\'t just for the wealthy. Minimum: will (who gets what), power of attorney (who makes decisions if you\'re incapacitated), beneficiaries updated on all accounts. Do it in your 30s, not "someday."'
    },
    {
      num: 4,
      title: 'Tax-Efficient Strategies',
      story: 'Morgan puts $10K in a regular brokerage account, earns $1,000 in gains, pays $200 in taxes. Alex puts $10K in a Roth IRA, earns $1,000 in gains, pays $0 in taxes (now or ever). Over 30 years, Alex saves $50K+ just by using the right account.',
      lesson: 'Tax-advantaged accounts = free returns. 401(k) & Traditional IRA (pay taxes later), Roth IRA & Roth 401(k) (pay taxes now, grow tax-free forever). HSA = triple tax advantage. Use these BEFORE regular brokerage accounts.'
    },
    {
      num: 5,
      title: 'Setting SMART Goals',
      story: 'Every January, Quinn says "I want to save more money." By March, nothing changes. Then Quinn tries: "Save $500/month for 12 months = $6,000 emergency fund by Dec 31." Quinn tracks it monthly, hits the goal in November.',
      lesson: 'Vague goals fail. Make them SMART: Specific ($6K, not "more"), Measurable (track monthly), Achievable ($500/mo fits budget), Relevant (aligns with values), Time-bound (deadline = Dec 31). Write it down, review weekly.'
    },
    {
      num: 6,
      title: 'Building Your Action Plan',
      story: 'Skyler spends 8 hours researching the "perfect" investment strategy but never starts. River spends 1 hour creating a simple plan: (1) automate $200/month to HYSA, (2) increase 401(k) to get full match, (3) open Roth IRA in 90 days. River is $600 ahead in month one.',
      lesson: 'Done beats perfect. Your 90-day plan: Month 1 (emergency fund start, list debts), Month 2 (attack highest-interest debt, fix insurance gaps), Month 3 (open retirement accounts, write 1-year financial goals). Start now, adjust later.'
    }
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Header */}
      <div style={{ background: IG.navy, borderRadius: 12, padding: '28px 32px', textAlign: 'center' }}>
        <div style={{ fontSize: 12, color: C.mint, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 8 }}>Week 4</div>
        <div style={{ fontFamily: "'DM Serif Display',serif", fontSize: 32, color: '#fff', fontWeight: 400, marginBottom: 8 }}>Protection & Planning</div>
        <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.75)' }}>Safeguard what you've built and plan for the future</div>
      </div>

      {/* All Topics */}
      {topics.map(topic => (
        <InfoCard key={topic.num} style={{ borderLeft: `4px solid ${IG.navy}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
            <div style={{ width: 32, height: 32, borderRadius: '50%', background: IG.navy, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, flexShrink: 0 }}>{topic.num}</div>
            <div style={{ fontFamily: "'DM Serif Display',serif", fontSize: 18, color: IG.navy }}>{topic.title}</div>
          </div>
          <div style={{ background: '#f8f0ff', borderRadius: 8, padding: '14px 16px', marginBottom: 12 }}>
            <div style={{ fontSize: 13, color: C.text, lineHeight: 1.7, marginBottom: 8 }}>📖 {topic.story}</div>
          </div>
          <div style={{ fontSize: 14, color: C.text, lineHeight: 1.7, paddingLeft: 12 }}>
            <strong>💡 The Lesson:</strong> {topic.lesson}
          </div>
        </InfoCard>
      ))}
    </div>
  );
}

// ─── Exported map ─────────────────────────────────────────────────────────────
export const WEEK_INFOGRAPHICS = [
  { week: 0, component: Week1Infographic },
  { week: 1, component: Week2Infographic },
  { week: 2, component: Week3Infographic },
  { week: 3, component: Week4Infographic },
];

export default function WeekInfographic({ week, onBegin }) {
  const entry    = WEEK_INFOGRAPHICS.find(w => w.week === week);
  const Graphic  = entry?.component;

  return (
    <div style={{ minHeight: '100vh', background: C.bg, fontFamily: "'DM Sans', sans-serif" }}>
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '32px 24px 40px' }}>
        {Graphic && <Graphic />}

        {/* Begin Session CTA */}
        <div style={{ marginTop: 28, display: 'flex', justifyContent: 'center' }}>
          <button
            onClick={onBegin}
            style={{
              background: C.teal, color: '#fff', border: 'none', borderRadius: 10,
              padding: '14px 36px', fontSize: 16, fontWeight: 700, cursor: 'pointer',
              fontFamily: "'DM Sans', sans-serif", letterSpacing: '0.02em',
              boxShadow: '0 4px 16px rgba(13,148,136,0.28)',
            }}
            onMouseEnter={e => e.currentTarget.style.background = '#0b8278'}
            onMouseLeave={e => e.currentTarget.style.background = C.teal}
          >
            Begin Session →
          </button>
        </div>
      </div>
    </div>
  );
}
